import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);
  const polls = useRef(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!session_id) {
      setStatus("missing");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${session_id}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPlan(data.lookup_key);
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        polls.current += 1;
        if (polls.current > 20) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 1500);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [session_id, refresh]);

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-[#131c2e] border border-slate-800 rounded-2xl p-8 text-center space-y-5">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto" />
            <h1 className="font-heading text-xl font-bold text-slate-100">Confirming your payment…</h1>
            <p className="text-sm text-slate-400">Hang on a few seconds — Stripe is wrapping up.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-slate-50">You&apos;re in.</h1>
            <p className="text-sm text-slate-400">
              Your <span className="text-amber-300 font-mono-digit">{plan}</span> is active. A receipt is on
              its way to your email.
            </p>
            <Link
              to="/"
              data-testid="success-return-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold"
            >
              Open Hit Path <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {(status === "failed" || status === "missing" || status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-100">Payment not confirmed</h1>
            <p className="text-sm text-slate-400">
              {status === "timeout"
                ? "Taking longer than expected. If your card was charged, email support@thejerseyfellas.com and we'll sort it."
                : "No charge was captured. You can try again from the Upgrade page."}
            </p>
            <Link
              to="/upgrade"
              className="inline-block h-11 leading-[2.75rem] px-5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-heading font-bold"
            >
              Back to plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen bg-[#0b0f17] text-slate-100 flex items-center justify-center px-4">
    <div className="max-w-md w-full bg-[#131c2e] border border-slate-800 rounded-2xl p-8 text-center space-y-5">
      <XCircle className="w-12 h-12 text-slate-400 mx-auto" />
      <h1 className="font-heading text-2xl font-bold text-slate-100">Checkout cancelled</h1>
      <p className="text-sm text-slate-400">No charge was made. You can come back any time.</p>
      <div className="flex gap-3 justify-center">
        <Link
          to="/upgrade"
          className="h-11 leading-[2.75rem] px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading font-bold"
        >
          See plans
        </Link>
        <Link
          to="/"
          className="h-11 leading-[2.75rem] px-5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-heading font-bold"
        >
          Keep using free
        </Link>
      </div>
    </div>
  </div>
);
